import { Grid, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { Flex } from "~/components/Flex";
import { SpaceScope } from "~/hooks/useScope";
import {
  ExceptionReviewAction,
  ExceptionStatus,
  ListExceptionGroupsOrder,
  ListExceptionGroupsOrderField,
  ListSpaceExceptionsDocument,
  OrderDirection,
  useApplyExceptionExtensionReviewMutation,
  useApplyExceptionReviewMutation,
} from "~/operations";
import { useSpaceExceptions } from "./hooks/useSpaceExceptions";
import { FINDINGS_TABLE_INITIAL_PAGE_RANGE } from "~/components/pagination";
import { useSnackbar } from "notistack";
import { AllExceptionList } from "./components/AllExceptionList";
import { ExceptionGroupRowProps } from "./components/ExceptionsGroupRow";
import { PendingExceptionsCard } from "./components/PendingExceptionsCard";
import { ExpiringExceptionsCard } from "./components/ExpiringExceptionsCard";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { Paginate } from "~/components/pagination/paginate";
import { usePaginate } from "~/components/pagination/usePaginate";
import { EmptyExceptionsList } from "./components/EmptyCards";
import { useState } from "react";
import Breadcrumbs from "~/components/Breadcrumbs/Breadcrumbs";
import { setDocumentTitle } from "~/lib/commonUtils";
import { Loading } from "~/components/loading";

const breadcrumbItems = [
  {
    title: "Exceptions",
    key: "/space/exceptions",
  },
];

export type ExceptionsPageProps = { space: SpaceScope };

export function ExceptionsPage({ space }: ExceptionsPageProps) {
  const [searchParams] = useSearchParams();
  const exceptionId = searchParams.get("exceptionId");
  const { enqueueSnackbar } = useSnackbar();

  const paginate = usePaginate({
    defaultPageSize: FINDINGS_TABLE_INITIAL_PAGE_RANGE.to,
  });
  const baseUrl = paginate.resetCursorUrl(location.href);

  const sort = useSort<ListExceptionGroupsOrder>({
    baseUrl,
    defaultSort: {
      field: ListExceptionGroupsOrderField.CreatedAt,
      direction: OrderDirection.Desc,
    },
    validFields: [
      ListExceptionGroupsOrderField.CreatedAt,
      ListExceptionGroupsOrderField.ValidUntil,
    ],
  });

  const { allExceptions, filtering } = useSpaceExceptions({
    scopeMrn: space.mrn,
    sort,
    paginate,
  });
  const { filterForm } = filtering;

  const [applyExceptionReview, { loading: isReviewLoading }] =
    useApplyExceptionReviewMutation();
  const [applyExceptionExtensionReview, { loading: isExtensionReviewLoading }] =
    useApplyExceptionExtensionReviewMutation();
  const [loadingAction, setLoadingAction] = useState<
    ExceptionReviewAction | undefined
  >();
  const [loadingExceptionId, setLoadingExceptionId] = useState<
    string | undefined
  >();

  const handleReviewActionChange: ExceptionGroupRowProps["onReviewActionChange"] =
    async (exceptionGroup, action) => {
      setLoadingAction(action);
      setLoadingExceptionId(exceptionGroup.id);

      const isPendingExtension =
        exceptionGroup.status === ExceptionStatus.NeedsExtension;

      try {
        if (isPendingExtension) {
          await applyExceptionExtensionReview({
            variables: {
              input: {
                spaceMrn: space.mrn,
                exceptionId: exceptionGroup.exceptionId,
                action,
              },
            },
            refetchQueries: [ListSpaceExceptionsDocument],
            awaitRefetchQueries: true,
            notifyOnNetworkStatusChange: true,
            onCompleted: () => {
              enqueueSnackbar(
                "Exception extension review applied successfully",
                {
                  variant: "success",
                },
              );
            },
          });
        } else {
          await applyExceptionReview({
            variables: {
              input: {
                scopeMrn: exceptionGroup.scopeMrn,
                exceptionId: exceptionGroup.id,
                action,
              },
            },
            awaitRefetchQueries: true,
            notifyOnNetworkStatusChange: true,
            refetchQueries: [ListSpaceExceptionsDocument],
            onCompleted: () => {
              enqueueSnackbar("Exception review applied successfully", {
                variant: "success",
              });
            },
          });
        }
      } catch (error) {
        enqueueSnackbar("Failed to apply exception review", {
          variant: "error",
        });
      } finally {
        setLoadingAction(undefined);
        setLoadingExceptionId(undefined);
      }
    };

  const allExceptionGroups =
    allExceptions.data?.listExceptionGroups.edges?.flatMap(
      (eg) => eg.node ?? [],
    ) ?? [];

  const allExceptionsEmpty = allExceptionGroups.length === 0;
  const isLoading = allExceptions.loading;

  const paginationProps = paginate.pageProps(
    allExceptions.data?.listExceptionGroups.pageInfo,
  );

  setDocumentTitle(["Exceptions", space.name]);

  return (
    <>
      <Breadcrumbs items={breadcrumbItems} />
      <Flex justifyContent="space-between" alignItems="center" sx={{ mb: 5 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
          }}
        >
          Exceptions
        </Typography>
      </Flex>
      <Grid container spacing={3}>
        <Grid size={12} container spacing={3}>
          <Grid size={6}>
            <PendingExceptionsCard
              spaceScope={space}
              onReviewActionChange={handleReviewActionChange}
              isReviewLoading={isReviewLoading}
              loadingAction={loadingAction}
              loadingExceptionId={loadingExceptionId}
            />
          </Grid>
          <Grid size={6}>
            <ExpiringExceptionsCard spaceScope={space} baseUrl={baseUrl} />
          </Grid>
        </Grid>
        <Grid size={12}>{filterForm}</Grid>
        {isLoading && (
          <Flex
            justifyContent="center"
            alignItems="center"
            sx={{ width: "100%" }}
          >
            <Loading what="Exceptions" />
          </Flex>
        )}

        {!isLoading && allExceptionsEmpty && <EmptyExceptionsList />}

        {!allExceptionsEmpty && !isLoading && (
          <Grid size={12}>
            <AllExceptionList
              {...{
                allExceptionGroups,
                handleReviewActionChange,
                exceptionId,
                orderBy: sort.orderBy,
                sortColumnUrl: (field) =>
                  paginate.resetCursorUrl(sort.sortColumnUrl(field)),
                isReviewLoading: isReviewLoading || isExtensionReviewLoading,
                loadingAction,
                loadingExceptionId,
                refetch: allExceptions.refetch,
              }}
            />
            <Paginate {...paginationProps} />
          </Grid>
        )}
      </Grid>
    </>
  );
}
